import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  PiBuildingsLight,
  PiCheckCircleFill,
  PiCircleDashedLight,
  PiClockLight,
  PiFingerprintLight,
  PiScalesLight,
  PiShieldCheckLight,
  PiWarningCircleFill,
} from 'react-icons/pi';
import { useAppDispatch } from '../../store';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import GlassCard from '../shared/brand/GlassCard';
import Button from '../shared/TailwindComponents/Button';
import Dropdown from '../shared/TailwindComponents/Dropdown';
import Input from '../shared/TailwindComponents/Input';
import Skeleton from '../shared/TailwindComponents/Skeleton';
import {
  LawyerVerification,
  LicenceCountry,
  LicenceType,
  VerificationStepStatus,
  errorMessage,
  getMyVerification,
  setVerificationCountry,
  startBankIdVerification,
  submitPractice,
} from '../../services/api/marketplaceService';
import { countryName, fmtDate } from './marketplaceUi';

/** The staff reviewer's rejection code, in words the lawyer can act on. */
export const licenceRejectionText = (t: TFunction, code: string | null | undefined, country?: string): string =>
  ({
    not_in_register: country === 'ES'
      ? t("We couldn't find you in the CGAE Censo de Letrados.")
      : t("We couldn't find you in Advokattilsynet's register."),
    name_mismatch: t("Your BankID name doesn't match the register."),
    licence_inactive: t('The register shows your licence as not active.'),
    firm_mismatch: t("The firm doesn't match the register."),
  } as Record<string, string>)[code || ''] ?? t("We couldn't confirm your licence.");

const firmProblemText = (t: TFunction, reason: string | null | undefined): string =>
  ({
    not_found: t("Brønnøysundregistrene has no organisation with that number."),
    deleted: t('Brønnøysundregistrene lists this organisation as deleted.'),
    bankrupt: t('Brønnøysundregistrene lists this organisation as bankrupt.'),
    being_wound_up: t('Brønnøysundregistrene lists this organisation as being wound up.'),
  } as Record<string, string>)[reason || ''] ?? t('This organisation could not be confirmed.');

const StepMark: React.FC<{ status: VerificationStepStatus }> = ({ status }) =>
  status === 'passed' ? <PiCheckCircleFill className="h-5 w-5 text-success-600" />
    : status === 'failed' ? <PiWarningCircleFill className="h-5 w-5 text-danger-600" />
      : status === 'pending' ? <PiClockLight className="h-5 w-5 text-warning-600" />
        : <PiCircleDashedLight className="h-5 w-5 text-fg-muted" />;

const Step: React.FC<{
  status: VerificationStepStatus; icon: React.ReactNode; title: string; children: React.ReactNode; testId: string;
}> = ({ status, icon, title, children, testId }) => (
  <li className="flex gap-3 py-4 first:pt-0 last:pb-0" data-testid={testId} data-status={status}>
    <div className="pt-0.5"><StepMark status={status} /></div>
    <div className="min-w-0 flex-1 space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-fg-strong">
        <span className="text-fg-muted [&>svg]:h-4 [&>svg]:w-4">{icon}</span>
        {title}
      </div>
      <div className="space-y-3 text-sm text-fg-body">{children}</div>
    </div>
  </li>
);

/**
 * The lawyer's verification, per licence country:
 *  NO: BankID → firm org number (Brønnøysund) → PONS checks Advokattilsynet's register.
 *  ES: eID → NIF/CIF + colegio + número de colegiado → PONS checks the CGAE census. Only a verified lawyer can send
 * proposals or be suggested to clients. `onChange` fires with each new state.
 */
const LawyerVerificationPanel: React.FC<{ onChange?: (v: LawyerVerification) => void }> = ({ onChange }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [v, setV] = useState<LawyerVerification | null>(null);
  const [orgNumber, setOrgNumber] = useState('');
  const [licenceType, setLicenceType] = useState<LicenceType>('advokat');
  const [supervisor, setSupervisor] = useState('');
  const [taxId, setTaxId] = useState('');
  const [firmName, setFirmName] = useState('');
  const [barAssociation, setBarAssociation] = useState('');
  const [licenceNumber, setLicenceNumber] = useState('');
  const [busy, setBusy] = useState<'bankid' | 'firm' | 'country' | null>(null);

  const fail = useCallback((err: unknown, fallback: string) => dispatch(showFeedbackModal({
    showModal: true, modalType: 'error', duration: 5000, message: errorMessage(err, fallback),
  })), [dispatch]);

  // A ref, not a dependency: callers pass an inline callback that changes
  // every render, and the load effect below must run once.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const apply = useCallback((next: LawyerVerification) => {
    setV(next);
    onChangeRef.current?.(next);
    if (next.firm.licence_type) setLicenceType(next.firm.licence_type);
  }, []);

  useEffect(() => {
    getMyVerification().then(apply).catch((err) => fail(err, t('Could not load your verification')));
  }, [apply, fail, t]);

  const startBankId = async () => {
    setBusy('bankid');
    try {
      const { authorize_url: url } = await startBankIdVerification();
      window.location.assign(url);
    } catch (err) {
      fail(err, t('Could not start BankID'));
      setBusy(null);
    }
  };

  const es = v?.country === 'ES';
  const orgDigits = orgNumber.replace(/\s/g, '');
  const firmReady = es
    ? taxId.trim().length >= 9 && firmName.trim() !== '' && barAssociation.trim() !== '' && licenceNumber.trim() !== ''
    : /^\d{9}$/.test(orgDigits) && (licenceType === 'advokat' || supervisor.trim().length > 0);

  const changeCountry = async (country: LicenceCountry) => {
    setBusy('country');
    try {
      apply(await setVerificationCountry(country));
    } catch (err) {
      fail(err, t('Could not change the country'));
    } finally {
      setBusy(null);
    }
  };

  const sendFirm = async () => {
    setBusy('firm');
    try {
      apply(await submitPractice(es ? {
        tax_id: taxId.trim(),
        firm_name: firmName.trim(),
        bar_association: barAssociation.trim(),
        licence_number: licenceNumber.trim(),
      } : {
        org_number: orgDigits,
        licence_type: licenceType,
        ...(licenceType === 'advokatfullmektig' ? { supervising_lawyer: supervisor.trim() } : {}),
      }));
    } catch (err) {
      fail(err, es ? t('Could not save your details') : t('Could not check the organisation number'));
    } finally {
      setBusy(null);
    }
  };

  if (!v) return <Skeleton variant="card" />;

  const { identity, firm, licence } = v;
  // After a rejection the lawyer may correct either step; that re-queues review.
  const rejected = licence.status === 'failed';
  const showFirmForm = firm.status !== 'passed' || rejected;

  return (
    <GlassCard
      icon={<PiShieldCheckLight />}
      title={v.status === 'verified' ? t('Your lawyer profile is verified') : t('Verify your lawyer profile')}
      subtitle={t('Only verified lawyers can send proposals, and clients only see verified lawyers.')}
    >
      {/* The country decides every step below; fixed once approved. */}
      {v.status !== 'verified' && (v.countries?.length ?? 0) > 1 && (
        <div className="mb-4 max-w-xs">
          <Dropdown
            id="verification-country"
            label={t('Where are you licensed?')}
            options={(v.countries || []).map((c) => ({ value: c, label: countryName(t, c) }))}
            value={v.country || 'NO'}
            onChange={(val) => changeCountry(val as LicenceCountry)}
            fullWidth
          />
        </div>
      )}
      <ol className="divide-y divide-hairline-soft" data-testid="marketplace-verification">
        <Step status={identity.status} icon={<PiFingerprintLight />}
          title={es ? t('Confirm your identity') : t('Confirm your identity with BankID')}
          testId="verification-identity">
          {identity.status === 'passed' && (identity.provider === 'stub' ? (
            <p data-testid="verification-test-login">
              {t('Test login as {{name}}. This is not a real identity check.', { name: identity.name })}
            </p>
          ) : (
            <p>{t('Confirmed as {{name}}.', { name: identity.name })}</p>
          ))}
          {(identity.status !== 'passed' || rejected) && (
            <>
              {identity.status === 'failed' && (
                <p className="text-danger-600">{es ? t("The identity check didn't complete. Try again.") : t("BankID didn't complete. Try again.")}</p>
              )}
              {v.bankid_available ? (
                <div>
                  <Button variant="primary" size="small" onClick={startBankId} isLoading={busy === 'bankid'}
                    disabled={busy !== null}>
                    {v.bankid_mode === 'stub' ? t('Use test login (not a real identity check)')
                      : es ? t('Verify your identity') : t('Verify with BankID')}
                  </Button>
                </div>
              ) : (
                <p className="text-fg-muted">
                  {es ? t("Identity verification for Spain isn't available yet.") : t("BankID verification isn't available yet.")}
                </p>
              )}
            </>
          )}
        </Step>

        <Step status={firm.status} icon={<PiBuildingsLight />} title={t('Your firm and licence')} testId="verification-firm">
          {firm.status === 'passed' && (es ? (
            <p>
              {firm.name} · {t('NIF {{number}}', { number: firm.tax_id })} · {firm.bar_association}
              {' · '}{t('No. {{number}}', { number: firm.licence_number })}
            </p>
          ) : (
            <p>
              {firm.name} · {t('Org. no. {{number}}', { number: firm.org_number })}
              {firm.licence_type && ` · ${firm.licence_type === 'advokat' ? t('Advokat') : t('Advokatfullmektig')}`}
            </p>
          ))}
          {firm.status === 'failed' && <p className="text-danger-600">{firmProblemText(t, firm.reason)}</p>}
          {showFirmForm && es && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input type="text" id="verification-tax-id" label={t('NIF, CIF or NIE')} placeholder="B12345674"
                value={taxId} onChange={(e) => setTaxId(e.target.value.slice(0, 15))} />
              <Input type="text" id="verification-firm-name" label={t('Law firm (or your own name)')}
                value={firmName} onChange={(e) => setFirmName(e.target.value.slice(0, 200))} />
              <Input type="text" id="verification-bar" label={t('Colegio de la Abogacía')}
                placeholder={t('e.g. Ilustre Colegio de la Abogacía de Madrid')}
                value={barAssociation} onChange={(e) => setBarAssociation(e.target.value.slice(0, 200))} />
              <Input type="text" id="verification-licence-number" label={t('Número de colegiado')}
                value={licenceNumber} onChange={(e) => setLicenceNumber(e.target.value.slice(0, 20))} />
              <div className="flex items-end md:col-span-2 md:justify-end">
                <Button variant="primary" size="small" onClick={sendFirm} isLoading={busy === 'firm'}
                  disabled={busy !== null || !firmReady}>
                  {t('Save details')}
                </Button>
              </div>
            </div>
          )}
          {showFirmForm && !es && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Input
                type="text"
                id="verification-org-number"
                label={t("Firm's organisation number")}
                placeholder="123 456 789"
                inputMode="numeric"
                value={orgNumber}
                onChange={(e) => setOrgNumber(e.target.value.slice(0, 11))}
              />
              <Dropdown
                id="verification-licence-type"
                label={t('Your title')}
                options={[
                  { value: 'advokat', label: t('Advokat') },
                  { value: 'advokatfullmektig', label: t('Advokatfullmektig') },
                ]}
                value={licenceType}
                onChange={(val) => setLicenceType(val as LicenceType)}
                fullWidth
              />
              {licenceType === 'advokatfullmektig' && (
                <Input
                  type="text"
                  id="verification-supervisor"
                  label={t('Supervising lawyer')}
                  placeholder={t('Full name of the advokat you work under')}
                  value={supervisor}
                  onChange={(e) => setSupervisor(e.target.value.slice(0, 200))}
                />
              )}
              <div className="flex items-end md:col-span-2 md:justify-end">
                <Button variant="primary" size="small" onClick={sendFirm} isLoading={busy === 'firm'}
                  disabled={busy !== null || !firmReady}>
                  {t('Check organisation')}
                </Button>
              </div>
            </div>
          )}
        </Step>

        <Step status={licence.status} icon={<PiScalesLight />} title={t('PONS checks your licence')}
          testId="verification-licence">
          {licence.status === 'missing' && <p className="text-fg-muted">{t('Starts when the two steps above are done.')}</p>}
          {licence.status === 'pending' && (
            <p>{es
              ? t("We're checking the CGAE Censo de Letrados. You'll get a notification when it's done.")
              : t("We're checking Advokattilsynet's register. You'll get a notification when it's done.")}</p>
          )}
          {licence.status === 'passed' && (v.recheck_due ? (
            <p>{t('Your approval has expired. PONS is checking your licence again.')}</p>
          ) : (
            <p>{t('Verified until {{date}}.', { date: fmtDate(v.verified_until) })}</p>
          ))}
          {licence.status === 'failed' && (
            <>
              <p className="text-danger-600">{licenceRejectionText(t, licence.reason, v.country)}</p>
              <p className="text-fg-muted">
                {t('Correct your details above or verify your identity again, and PONS will review it again. Questions: support@pons.io.')}
              </p>
            </>
          )}
        </Step>
      </ol>
    </GlassCard>
  );
};

export default LawyerVerificationPanel;
